import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import AdminLayout from "../components/AdminLayout";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
  writeBatch,
} from "firebase/firestore";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Autocomplete,
  Typography,
  Box,
  createFilterOptions,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import {
  LocalFireDepartment as FireIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DeleteSweep as ClearIcon,
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

// ─── Styled Components ────────────────────────────────────────────────────────

const PageContainer = styled("div")`
  display: flex;
  flex-direction: column;
  gap: var(--space-xl);
`;

const PageHeader = styled("div")`
  margin-bottom: var(--space-lg);
`;

const PageTitle = styled("h1")`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: var(--space-md);
`;

const StatsRow = styled("div")`
  display: flex;
  gap: var(--space-md);
  flex-wrap: wrap;
  margin-bottom: var(--space-lg);
`;

const StatCard = styled("div")`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: var(--space-lg);
  min-width: 160px;
  flex: 1;
`;

const StatValue = styled("div")`
  font-size: 2rem;
  font-weight: 700;
  color: ${(props) => props.color || "var(--text-primary)"};
`;

const StatLabel = styled("div")`
  font-size: 0.8rem;
  color: var(--text-secondary);
  margin-top: 4px;
`;

const ToolbarRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
  margin-bottom: var(--space-md);
`;

const DataGridContainer = styled("div")`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  overflow: hidden;

  .MuiDataGrid-root {
    border: none;
    background: transparent;
    color: var(--text-primary);
  }

  .MuiDataGrid-columnHeaders {
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border);
  }

  .MuiDataGrid-columnHeader {
    color: var(--text-primary);
    font-weight: 600;
  }

  .MuiDataGrid-row {
    border-bottom: 1px solid var(--border-subtle);

    &:hover {
      background: var(--surface-hover);
    }
  }

  .MuiDataGrid-cell {
    color: var(--text-secondary);
    border-bottom: none;
  }

  .MuiDataGrid-footerContainer {
    background: var(--bg-tertiary);
    border-top: 1px solid var(--border);
    color: var(--text-secondary);
  }
`;

const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: "#10b981", dark: "#059669", light: "#34d399" },
    secondary: { main: "#06b6d4" },
    error: { main: "#ef4444" },
    success: { main: "#22c55e" },
    warning: { main: "#f59e0b" },
    background: { default: "#0a0f0a", paper: "#2d342d" },
    text: { primary: "#f0f9f0", secondary: "#c8d8c8" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          textTransform: "none",
          fontWeight: 600,
          padding: "12px 24px",
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 10px 25px rgba(0,0,0,0.3)",
          },
        },
        outlined: {
          borderColor: "#10b981",
          color: "#10b981",
          "&:hover": {
            borderColor: "#34d399",
            backgroundColor: "rgba(16,185,129,0.1)",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#2d342d",
          backgroundImage: "none",
          border: "1px solid #374151",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#10b981" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#10b981" },
          },
        },
      },
    },
  },
});

// ─── Product search filter (searches name + category) ─────────────────────────

const productFilterOptions = createFilterOptions({
  matchFrom: "any",
  stringify: (p) => `${p.name} ${p.category_name || ""}`,
});

// ─── Helpers ──────────────────────────────────────────────────────────────────

const dealBadgeColor = (pct) => {
  if (pct >= 30) return "#ef4444";
  if (pct >= 15) return "#f97316";
  return "#f59e0b";
};

const fmtDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d.toLocaleDateString("en-MY", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const isDealExpired = (ts) => {
  if (!ts) return false;
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  return d <= new Date();
};

const isExpiringSoon = (ts) => {
  if (!ts) return false;
  const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
  const soon = new Date(Date.now() + 3 * 86400000);
  return d > new Date() && d <= soon;
};

const EMPTY_FORM = {
  deal_mode: "timed",
  promotional_price: "",
  deal_expiry_days: 7,
  deal_label_override: "",
  new_price: "",
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function Promotions() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState("all");
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productSearch, setProductSearch] = useState(null);
  const [dealForm, setDealForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchData = async () => {
    setLoading(true);
    try {
      const [prodSnap, catSnap] = await Promise.all([
        getDocs(collection(db, "products")),
        getDocs(collection(db, "categories")),
      ]);

      const catMap = {};
      catSnap.docs.forEach((d) => {
        catMap[d.id] = d.data().name || d.id;
      });
      setCategories(catSnap.docs.map((d) => ({ id: d.id, name: d.data().name || d.id })));

      setProducts(
        prodSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          category_name: catMap[d.data().category_id] || "—",
        }))
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Filtered products by tab ───────────────────────────────────────────────

  const filteredProducts = useMemo(() => {
    switch (tab) {
      case "active":
        return products.filter(
          (p) => p.is_deal && !isDealExpired(p.deal_expires_at)
        );
      case "expiring":
        return products.filter(
          (p) => p.is_deal && isExpiringSoon(p.deal_expires_at)
        );
      case "expired":
        return products.filter(
          (p) => p.is_deal && isDealExpired(p.deal_expires_at)
        );
      default:
        return products;
    }
  }, [products, tab]);

  // ── Stats ──────────────────────────────────────────────────────────────────

  const activeDeals = products.filter(
    (p) => p.is_deal && !isDealExpired(p.deal_expires_at)
  ).length;
  const expiringSoon = products.filter(
    (p) => p.is_deal && isExpiringSoon(p.deal_expires_at)
  ).length;
  const timedPromos = products.filter(
    (p) => p.is_deal && p.is_timed_promotion && !isDealExpired(p.deal_expires_at)
  ).length;
  const expiredDeals = products.filter(
    (p) => p.is_deal && isDealExpired(p.deal_expires_at)
  ).length;

  // ── Open dialog ────────────────────────────────────────────────────────────

  const openCreate = () => {
    setSelectedProduct(null);
    setProductSearch(null);
    setDealForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (product) => {
    setSelectedProduct(product);
    setProductSearch(product);
    setDealForm({
      deal_mode: product.is_timed_promotion ? "timed" : "permanent_override",
      promotional_price: product.promotional_price?.toString() || "",
      deal_expiry_days: 7,
      deal_label_override: product.deal_label || "",
      new_price: product.is_timed_promotion ? "" : (product.price?.toString() || ""),
    });
    setDialogOpen(true);
  };

  // ── Save deal ──────────────────────────────────────────────────────────────

  const handleSaveDeal = async () => {
    const product = selectedProduct || productSearch;
    if (!product) return;

    setSaving(true);
    try {
      const now = new Date();
      const expiresAt = new Date(
        now.getTime() + (dealForm.deal_expiry_days || 7) * 86400000
      );

      if (dealForm.deal_mode === "timed") {
        const promoPrice = parseFloat(dealForm.promotional_price);
        if (isNaN(promoPrice) || promoPrice <= 0) {
          alert("Please enter a valid promotional price.");
          setSaving(false);
          return;
        }
        const dropPct = ((product.price - promoPrice) / product.price) * 100;
        const label =
          dealForm.deal_label_override ||
          (dropPct >= 30
            ? "Jimat Besar!"
            : dropPct >= 15
            ? "Price Drop Kaw-Kaw!"
            : "Harga Turun");

        await updateDoc(doc(db, "products", product.id), {
          is_deal: true,
          is_timed_promotion: true,
          promotional_price: promoPrice,
          previous_price: product.price,
          price_drop_percent: parseFloat(dropPct.toFixed(1)),
          deal_label: label,
          deal_expires_at: Timestamp.fromDate(expiresAt),
          admin_override_deal: true,
        });
      } else {
        const newPrice = parseFloat(dealForm.new_price);
        if (!isNaN(newPrice) && newPrice > 0 && newPrice >= product.price) {
          alert(`New price must be lower than the original price (RM ${parseFloat(product.price).toFixed(2)}). To raise a price, edit the product from the Products page.`);
          setSaving(false);
          return;
        }
        const hasNewPrice = !isNaN(newPrice) && newPrice > 0 && newPrice !== product.price;
        const dropPct = hasNewPrice
          ? ((product.price - newPrice) / product.price) * 100
          : 0;
        const label =
          dealForm.deal_label_override ||
          (hasNewPrice
            ? dropPct >= 30
              ? "Jimat Besar!"
              : dropPct >= 15
              ? "Price Drop Kaw-Kaw!"
              : "Harga Turun"
            : "Special Deal");

        await updateDoc(doc(db, "products", product.id), {
          ...(hasNewPrice && { price: newPrice }),
          is_deal: true,
          is_timed_promotion: false,
          promotional_price: null,
          previous_price: hasNewPrice ? product.price : (product.previous_price || product.price),
          price_drop_percent: parseFloat(dropPct.toFixed(1)),
          deal_label: label,
          deal_expires_at: Timestamp.fromDate(expiresAt),
          admin_override_deal: true,
        });
      }

      setDialogOpen(false);
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  // ── Remove deal ────────────────────────────────────────────────────────────

  const handleRemoveDeal = async (product) => {
    const revertPrice =
      product.is_timed_promotion && product.previous_price
        ? product.previous_price
        : product.price;

    await updateDoc(doc(db, "products", product.id), {
      price: revertPrice,
      is_deal: false,
      is_timed_promotion: false,
      promotional_price: null,
      previous_price: null,
      price_drop_percent: 0,
      deal_label: "",
      deal_expires_at: null,
      admin_override_deal: false,
    });
    await fetchData();
  };

  // ── Clear expired ──────────────────────────────────────────────────────────

  const handleClearExpired = async () => {
    setConfirmClear(false);
    const batch = writeBatch(db);
    products.forEach((p) => {
      if (p.is_deal && isDealExpired(p.deal_expires_at)) {
        const revertPrice =
          p.is_timed_promotion && p.previous_price ? p.previous_price : p.price;
        batch.update(doc(db, "products", p.id), {
          price: revertPrice,
          is_deal: false,
          is_timed_promotion: false,
          promotional_price: null,
          previous_price: null,
          price_drop_percent: 0,
          deal_label: "",
          deal_expires_at: null,
          admin_override_deal: false,
        });
      }
    });
    await batch.commit();
    await fetchData();
  };

  // ── DataGrid columns ───────────────────────────────────────────────────────

  const columns = [
    { field: "name", headerName: "Product", width: 200, resizable: true },
    {
      field: "category_name",
      headerName: "Category",
      width: 130,
      resizable: true,
    },
    {
      field: "price",
      headerName: "Original Price",
      width: 130,
      renderCell: (p) => `RM ${parseFloat(p.value || 0).toFixed(2)}`,
    },
    {
      field: "promotional_price",
      headerName: "Deal Price",
      width: 120,
      renderCell: (p) =>
        p.value ? `RM ${parseFloat(p.value).toFixed(2)}` : "—",
    },
    {
      field: "price_drop_percent",
      headerName: "Drop %",
      width: 90,
      renderCell: (p) =>
        p.value > 0 ? (
          <span style={{ color: dealBadgeColor(p.value), fontWeight: "bold" }}>
            {p.value}%
          </span>
        ) : (
          "—"
        ),
    },
    {
      field: "deal_label",
      headerName: "Badge",
      width: 190,
      renderCell: (p) =>
        p.value ? (
          <Chip
            label={p.value}
            size="small"
            style={{
              backgroundColor: dealBadgeColor(p.row.price_drop_percent),
              color: "#fff",
              fontWeight: "bold",
              fontSize: "11px",
            }}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "is_timed_promotion",
      headerName: "Type",
      width: 120,
      renderCell: (p) =>
        p.row.is_deal ? (
          p.value ? (
            <span style={{ color: "#3b82f6", fontWeight: 600 }}>Timed</span>
          ) : (
            <span style={{ color: "#10b981", fontWeight: 600 }}>Permanent</span>
          )
        ) : (
          <span style={{ color: "#6b7280" }}>—</span>
        ),
    },
    {
      field: "deal_expires_at",
      headerName: "Expires",
      width: 140,
      renderCell: (p) => fmtDate(p.value),
    },
    {
      field: "is_deal",
      headerName: "Status",
      width: 110,
      renderCell: (p) => {
        if (!p.value)
          return <span style={{ color: "#6b7280" }}>No Deal</span>;
        const expired = isDealExpired(p.row.deal_expires_at);
        const expiring = isExpiringSoon(p.row.deal_expires_at);
        if (expired)
          return <span style={{ color: "#ef4444", fontWeight: 600 }}>Expired</span>;
        if (expiring)
          return <span style={{ color: "#f59e0b", fontWeight: 600 }}>Expiring Soon</span>;
        return <span style={{ color: "#10b981", fontWeight: 600 }}>Active</span>;
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      sortable: false,
      resizable: false,
      renderCell: (p) => (
        <>
          <IconButton
            size="small"
            color="primary"
            onClick={() => openEdit(p.row)}
            title={p.row.is_deal ? "Edit Deal" : "Create Deal"}
          >
            {p.row.is_deal ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </IconButton>
          {p.row.is_deal && (
            <IconButton
              size="small"
              color="error"
              onClick={() => handleRemoveDeal(p.row)}
              title="Remove Deal"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </>
      ),
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <ThemeProvider theme={darkTheme}>
    <AdminLayout>
      <PageContainer>
        {/* Header */}
        <PageHeader>
          <PageTitle>
            <FireIcon style={{ fontSize: "2.5rem", color: "#ef4444" }} />
            Promotions & Deals
          </PageTitle>
          <p style={{ color: "var(--text-secondary)", margin: 0 }}>
            Manage price drop deals and timed promotions for your products.
          </p>
        </PageHeader>

        {/* Stats */}
        <StatsRow>
          <StatCard>
            <StatValue color="#10b981">{activeDeals}</StatValue>
            <StatLabel>Active Deals</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#f59e0b">{expiringSoon}</StatValue>
            <StatLabel>Expiring in 3 Days</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#3b82f6">{timedPromos}</StatValue>
            <StatLabel>Timed Promotions</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#ef4444">{expiredDeals}</StatValue>
            <StatLabel>Expired (needs cleanup)</StatLabel>
          </StatCard>
        </StatsRow>

        {/* Tabs + Toolbar */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          textColor="inherit"
          TabIndicatorProps={{ style: { backgroundColor: "#10b981" } }}
          style={{ marginBottom: "8px" }}
        >
          <Tab value="all" label="All Products" />
          <Tab
            value="active"
            label={`Active Deals (${activeDeals})`}
            style={{ color: "#10b981" }}
          />
          <Tab
            value="expiring"
            label={`Expiring Soon (${expiringSoon})`}
            style={{ color: "#f59e0b" }}
          />
          <Tab
            value="expired"
            label={`Expired (${expiredDeals})`}
            style={{ color: "#ef4444" }}
          />
        </Tabs>

        <ToolbarRow>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            style={{ backgroundColor: "#ef4444" }}
          >
            Create Deal
          </Button>
          {expiredDeals > 0 && (
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              color="error"
              onClick={() => setConfirmClear(true)}
            >
              Clear Expired ({expiredDeals})
            </Button>
          )}
        </ToolbarRow>

        {/* DataGrid */}
        <DataGridContainer>
          <DataGrid
            rows={filteredProducts}
            columns={columns}
            loading={loading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            disableRowSelectionOnClick
            style={{ minHeight: 400, border: "none" }}
            sx={{
              "& .MuiDataGrid-row:hover": { backgroundColor: "var(--surface-hover)" },
              "& .MuiDataGrid-cell:focus": { outline: "none" },
            }}
          />
        </DataGridContainer>
      </PageContainer>

      {/* ── Create / Edit Deal Dialog ─────────────────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedProduct ? `Edit Deal — ${selectedProduct.name}` : "Create Deal"}
        </DialogTitle>
        <DialogContent>
          {/* Product selector (only for create) */}
          {!selectedProduct && (
            <Autocomplete
              options={products}
              getOptionLabel={(p) => `${p.name} (RM ${parseFloat(p.price || 0).toFixed(2)})`}
              filterOptions={productFilterOptions}
              value={productSearch}
              onChange={(_, v) => setProductSearch(v)}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderOption={(props, option) => {
                const { key, ...liProps } = props;
                return (
                  <li key={key ?? option.id} {...liProps} style={{ padding: 0 }}>
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      px: 2,
                      py: "10px",
                      gap: 2,
                    }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                          {option.category_name || "—"}
                          {option.is_deal && !isDealExpired(option.deal_expires_at) && (
                            <span style={{ marginLeft: 6, color: "#ef4444" }}>● Active Deal</span>
                          )}
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight={700}
                        sx={{ color: "#10b981", flexShrink: 0, whiteSpace: "nowrap" }}>
                        RM {parseFloat(option.price || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Product"
                  placeholder="Type name or category…"
                  margin="dense"
                  fullWidth
                  required
                />
              )}
              noOptionsText="No products found"
              clearOnEscape
            />
          )}

          {/* Show original price */}
          {(selectedProduct || productSearch) && (
            <Typography
              variant="body2"
              style={{ color: "var(--text-secondary)", margin: "8px 0" }}
            >
              Original price: RM{" "}
              {parseFloat(
                (selectedProduct || productSearch)?.price || 0
              ).toFixed(2)}
            </Typography>
          )}

          {/* Deal mode */}
          <FormControl fullWidth margin="dense">
            <InputLabel>Deal Mode</InputLabel>
            <Select
              value={dealForm.deal_mode}
              label="Deal Mode"
              onChange={(e) =>
                setDealForm((f) => ({ ...f, deal_mode: e.target.value }))
              }
            >
              <MenuItem value="timed">
                Timed Promotion — price reverts after expiry
              </MenuItem>
              <MenuItem value="permanent_override">
                Permanent Override — badge only, price stays
              </MenuItem>
            </Select>
          </FormControl>

          {/* Timed-specific fields */}
          {dealForm.deal_mode === "timed" && (
            <TextField
              label="Promotional Price (RM)"
              type="number"
              fullWidth
              margin="dense"
              value={dealForm.promotional_price}
              onChange={(e) =>
                setDealForm((f) => ({ ...f, promotional_price: e.target.value }))
              }
              helperText="Discounted price shown during the promotion. Original price restores automatically after expiry."
              inputProps={{ min: 0, step: "0.01" }}
              required
            />
          )}

          {/* Permanent override — optional new price */}
          {dealForm.deal_mode === "permanent_override" && (() => {
            const currentProduct = selectedProduct || productSearch;
            const entered = parseFloat(dealForm.new_price);
            const isHigher = !isNaN(entered) && entered > 0 && currentProduct && entered >= currentProduct.price;
            return (
              <TextField
                label="New Price (RM)"
                type="number"
                fullWidth
                margin="dense"
                value={dealForm.new_price}
                onChange={(e) =>
                  setDealForm((f) => ({ ...f, new_price: e.target.value }))
                }
                error={isHigher}
                helperText={
                  isHigher
                    ? `Price must be lower than the original (RM ${parseFloat(currentProduct.price).toFixed(2)}). To raise a price, use the Products page.`
                    : "Enter a lower price to permanently update it and auto-calculate the badge. Leave blank for badge only (price stays unchanged)."
                }
                inputProps={{ min: 0, step: "0.01" }}
              />
            );
          })()}

          {/* Duration */}
          <TextField
            label="Deal Duration (days)"
            type="number"
            fullWidth
            margin="dense"
            value={dealForm.deal_expiry_days}
            onChange={(e) =>
              setDealForm((f) => ({ ...f, deal_expiry_days: parseInt(e.target.value, 10) || 7 }))
            }
            inputProps={{ min: 1, max: 365 }}
            helperText={
              dealForm.deal_mode === "timed"
                ? "Price reverts to original after this period."
                : "Badge disappears after this period."
            }
          />

          {/* Custom badge label — dropdown templates + free type */}
          <Autocomplete
            freeSolo
            options={[
              "Harga Turun",
              "Price Drop Kaw-Kaw!",
              "Jimat Besar!",
              "Flash Sale!",
              "Special Deal",
              "Tawaran Istimewa",
              "Clearance Sale",
              "Weekend Special",
              "Members Only",
              "Limited Time!",
            ]}
            value={dealForm.deal_label_override}
            onChange={(_, v) =>
              setDealForm((f) => ({ ...f, deal_label_override: v || "" }))
            }
            onInputChange={(_, v) =>
              setDealForm((f) => ({ ...f, deal_label_override: v }))
            }
            renderOption={(props, option) => {
              const { key, ...liProps } = props;
              const color =
                option === "Jimat Besar!" ? "#ef4444"
                : option === "Price Drop Kaw-Kaw!" ? "#f97316"
                : option === "Harga Turun" ? "#f59e0b"
                : option === "Flash Sale!" ? "#8b5cf6"
                : "#6b7280";
              return (
                <li key={key ?? option} {...liProps} style={{ padding: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: "8px", width: "100%" }}>
                    <Box sx={{
                      px: 1, py: "2px",
                      borderRadius: "4px",
                      backgroundColor: color,
                      flexShrink: 0,
                    }}>
                      <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700, fontSize: "11px" }}>
                        {option}
                      </Typography>
                    </Box>
                  </Box>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Custom Badge Label (optional)"
                margin="dense"
                fullWidth
                placeholder="Select a template or type your own…"
                helperText="Leave blank to auto-generate from drop %. You can pick a template or write any custom label."
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveDeal}
            variant="contained"
            disabled={saving || (!selectedProduct && !productSearch)}
            style={{ backgroundColor: "#ef4444" }}
          >
            {saving ? "Saving..." : "Save Deal"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Clear Expired Dialog ──────────────────────────────── */}
      <Dialog open={confirmClear} onClose={() => setConfirmClear(false)}>
        <DialogTitle>Clear Expired Deals?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove deal badges from {expiredDeals} expired product(s).
            Timed promotions will have their original price restored.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClear(false)}>Cancel</Button>
          <Button
            onClick={handleClearExpired}
            variant="contained"
            color="error"
          >
            Clear {expiredDeals} Expired
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
    </ThemeProvider>
  );
}
